
import axios from 'axios';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BIconDownload } from 'bootstrap-vue';

import { LiveAgent } from '@gid/vue-common/components/ticketing/interface/ticketing';

@Component({ components: { BIconDownload } })
export default class LiveAgentAttachment extends Vue {
  @Prop({ default: () => ({}) }) attachment!: LiveAgent.IJsonAttachment;
  @Prop({ default: null }) id!: string;
  @Prop({ default: null }) ticketId!: string;

  downloading = false;

  get size(): string {
    return ((this.attachment.size || 0) / 1024).toFixed(2) + ' KB';
  }

  /**
   * LiveAgent won't serve an attachment to the browser directly — it needs the
   * api key — so gid-crud proxies the bytes. That endpoint identifies a file by
   * its index across *all* of the ticket's attachments, whereas the index we
   * render under is per-message, so the two diverge as soon as a second message
   * carries a file. Resolve ours against the ticket-wide list first, matching on
   * download_url (unique; names are not — two mails can both attach a
   * "protocol.pdf"). The name rides along as a guard: the endpoint returns 409
   * if the list shifted between the two calls.
   */
  async download() {
    if (!this.ticketId || this.downloading) return;
    this.downloading = true;
    try {
      const { data: attachments } = await axios.get(
        `/data-api/ticket/${this.ticketId}/attachments`,
      );
      const match = (attachments || []).find(
        (a) => a.download_url === this.attachment.download_url,
      );
      if (!match) {
        throw new Error(
          `"${this.attachment.name}" is no longer on ticket ${this.ticketId}`,
        );
      }

      const { data } = await axios.get(
        `/data-api/ticket/${this.ticketId}/attachments/download`,
        {
          params: { index: match.index, name: match.name },
          responseType: 'blob',
        },
      );
      this.saveBlob(data, match.name);
    } catch (e) {
      this.$bvToast.toast(
        this.$t('tickets.attachment_download_failed') as string,
        { variant: 'danger', title: this.attachment.name },
      );
    } finally {
      this.downloading = false;
    }
  }

  private saveBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on a later task, not immediately: the URL has to still resolve
    // when the browser gets around to handling the click it was just given.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}
